import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ContingencyPlanType, Response } from "../../../types";
import {
  Cto,
  CtoDashboard,
  RecommendationInfo,
  Recommendations,
} from "../../../types/cto/ctoTypes";
import type { State } from "../../../types/state/stateTypes";
import type { ControlStatus } from "../../../types/statusTypes";
import { translate } from "../../../intl";
import {
  deleteContingencyPlanSlice,
  getContingencyPlansSlice,
  postContingencyPlanSlice,
  putContingencyPlanSlice,
} from "../../../services/cto/ctoStep2Service";
import {
  fetchCtosByVesselDashboard,
  fetchRecommendationByVesselSlice,
} from "../../../services/cto/generic/ctoService";
import { handleFeedback } from "../../../services/generic/genericService";

export interface CtoState {
  step0: Cto;
  step2: Step2State;
  ctoList: Response<CtoDashboard[]>;
  recommendations: Response<RecommendationInfo[]>;
}

export interface Step2State {
  contingencyPlans: Response<ContingencyPlanType[]>;
}

interface ContingencyPlanBody {
  vesselCode: string;
  contingencyPlanCode?: string;
  body: ContingencyPlanType;
}

export const ctoSlice = createSlice({
  name: "cto",
  initialState: {
    step2: {},
    step0: {},
    ctoList: {},
    recommendations: {},
  } as CtoState,
  reducers: {
    setCto: (state, action) => {
      state = action.payload;
    },
    setRecommendations: {
      reducer: (state, action) => {
        state.recommendations = action.payload;
      },
      prepare: (payload) => {
        return {
          payload: {
            ...payload,
            data: (payload.data?.data || [])?.map(
              (recommendation: Recommendations) => ({
                ...recommendation,
              }),
            ),
          },
          meta: {},
          error: null,
        };
      },
    },
    setCtoList: {
      reducer: (state, action) => {
        state.ctoList = action.payload;
      },
      prepare: (payload) => {
        const serializedData = (payload?.data?.data || [])?.map(
          (cto: CtoDashboard) => ({
            ...cto,
            created_at: cto.created_at?.toISOString(),
            final_date: cto.final_date?.toISOString(),
            versions: cto.versions?.map((version) => ({
              ...version,
              version_date: version.version_date?.toString(),
            })),
          }),
        );

        return {
          payload: { ...payload, data: serializedData },
          meta: {},
          error: null,
        };
      },
    },
    setContingencyPlans: (state, action) => {
      state.step2.contingencyPlans = action.payload;
    },
    setCreateContingencyPlans: (state, action) => {
      state.step2.contingencyPlans = action.payload;
    },
    setStep0: (state, action) => {
      state.step0 = action.payload;
    },
  },
});

export const fetchCtoListByVessel = createAsyncThunk(
  "cto/ctoList",
  async (vessel_code: string, { dispatch }) => {
    dispatch(
      ctoSlice.actions.setCtoList({
        data: undefined,
        loading: true,
        error: false,
      }),
    );
    const res = await fetchCtosByVesselDashboard(vessel_code);
    dispatch(
      ctoSlice.actions.setCtoList({ data: res, loading: false, error: false }),
    );
  },
);

export const fetchCtoEpsListByVessel = createAsyncThunk(
  "cto/ctoList",
  async (vessel_code: string, { dispatch }) => {
    dispatch(
      ctoSlice.actions.setCtoList({
        data: undefined,
        loading: true,
        error: false,
      }),
    );
    const res = await fetchCtosByVesselDashboard(vessel_code);
    dispatch(
      ctoSlice.actions.setCtoList({ data: res, loading: false, error: false }),
    );
  },
);

export const fetchRecommendationsByVessel = createAsyncThunk(
  "cto/recommendations",
  async (
    {
      vessel_code,
      selectedCtoStatusIndex,
      selectedRecommendationStatus,
    }: {
      vessel_code: string;
      selectedCtoStatusIndex?: number;
      selectedRecommendationStatus?: ControlStatus;
    },
    { dispatch },
  ) => {
    dispatch(
      ctoSlice.actions.setRecommendations({
        data: undefined,
        loading: true,
        error: false,
      }),
    );
    const res = await fetchRecommendationByVesselSlice(
      vessel_code,
      selectedCtoStatusIndex,
      selectedRecommendationStatus,
    );
    dispatch(
      ctoSlice.actions.setRecommendations({
        data: res,
        loading: false,
        error: false,
      }),
    );
  },
);

export const fetchContingencyPlans = createAsyncThunk(
  "cto/step2/contingencyPlans",
  async (vessel_code: string, { dispatch }) => {
    dispatch(
      ctoSlice.actions.setContingencyPlans({
        data: undefined,
        loading: true,
        error: false,
      }),
    );
    const res = await getContingencyPlansSlice(vessel_code);
    dispatch(
      ctoSlice.actions.setContingencyPlans({
        data: res,
        loading: false,
        error: false,
      }),
    );
  },
);

export const createContingencyPlan = createAsyncThunk(
  "cto/step2/createContingencyPlan",
  async ({ vesselCode, body }: ContingencyPlanBody, { dispatch }) => {
    dispatch(
      ctoSlice.actions.setCreateContingencyPlans({
        data: undefined,
        loading: true,
        error: false,
      }),
    );
    const response = postContingencyPlanSlice(vesselCode, body);

    handleFeedback(response, (res) => {
      dispatch(
        ctoSlice.actions.setCreateContingencyPlans({
          data: res,
          loading: false,
          error: false,
        }),
      );
      dispatch(fetchContingencyPlans(vesselCode));
    });
  },
);

export const editContingencyPlan = createAsyncThunk(
  "cto/step2/editContingencyPlan",
  async (
    { vesselCode, contingencyPlanCode, body }: ContingencyPlanBody,
    { dispatch },
  ) => {
    dispatch(
      ctoSlice.actions.setCreateContingencyPlans({
        data: undefined,
        loading: true,
        error: false,
      }),
    );
    const response = putContingencyPlanSlice(
      vesselCode,
      body,
      contingencyPlanCode,
    );

    handleFeedback(response, (res) => {
      dispatch(
        ctoSlice.actions.setCreateContingencyPlans({
          data: res,
          loading: false,
          error: false,
        }),
      );
      dispatch(fetchContingencyPlans(vesselCode));
    });
  },
);

export const deleteContingencyPlan = createAsyncThunk(
  "cto/step2/deleteContingencyPlan",
  async (
    {
      vesselCode,
      contingencyPlanCode,
    }: { vesselCode: string; contingencyPlanCode: string },
    { dispatch },
  ) => {
    const response = deleteContingencyPlanSlice(
      vesselCode,
      contingencyPlanCode,
    );

    handleFeedback(
      response,
      () => dispatch(fetchContingencyPlans(vesselCode)),
      () => translate("feedback.deleted"),
    );
  },
);

export const clearCtoState = () => {
  ctoSlice.actions.setCto(ctoSlice.getInitialState());
};

export const ctoAllStepsSelector = (state: State) => state.cto;
export const ctoListSelector = (state: State) => state.cto.ctoList;
export const recommendationsSelector = (state: State) =>
  state.cto.recommendations;
export const ctoSelector = (state: State) => state.cto.step0;
export const step2Selector = (state: State) => state.cto.step2;
export const contingencyPlansSelector = (state: State) =>
  state.cto.step2.contingencyPlans;

export const actions = {
  ...ctoSlice.actions,
  ctoAllStepsSelector,
  ctoListSelector,
  recommendationsSelector,
  ctoSelector,
  step2Selector,
  contingencyPlansSelector,
  clearCtoState,
};

export const thunks = {
  fetchRecommendationsByVessel,
  fetchCtoListByVessel,
  fetchCtoEpsListByVessel,
  fetchContingencyPlans,
  createContingencyPlan,
  deleteContingencyPlan,
  editContingencyPlan,
};
